import menuItems, { NavItemWithChildren } from './index';
import { useCallback, useContext, useMemo } from 'react';
import { UserSubject } from '../models/IUser';
import { AbilityContext } from '../utils/roles/Can';
import useAuth from '../hooks/useAuth';
import useCanSeeSiteBuilder from '../views/site-builder/hooks/use-can-see-sitebuilder';
import { UserRole } from '../models/IEmployee';

const useAvailableMenuItems = () => {
    const { user } = useAuth();
    const legacyReviewManagement = Boolean(user?.currentCompany.cb_settings.cb.use_legacy_reputation_management);
    const ability = useContext(AbilityContext);

    const siteBuilderEnabled = useCanSeeSiteBuilder();
    const currentRole = user?.employee.role.name;
    const loyaltyProgramEnabled = user?.currentCompany.settings?.loyalty_program?.enabled;
    const isProviderOrFrontDesk = useMemo(
        () => [UserRole.Provider, UserRole.FrontDesk].some((role) => role === currentRole),
        [currentRole]
    );
    const productsEnabled = !!user?.currentCompany.settings?.appointments.use_products;

    const canSeeMenuItem = useCallback(
        (role_subject?: string, action = 'list') => (role_subject ? ability.can(action, role_subject as UserSubject) : true),
        [ability]
    );

    const filteredItems = useMemo(
        () =>
            menuItems.items.reduce<NavItemWithChildren[]>((prev, current) => {
                // Hide settings item for provider and Front desk user
                if (isProviderOrFrontDesk && current.id === 'settings-index') {
                    return prev;
                }

                if (current.children) {
                    const filteredChildren = current.children.filter((child) => {
                        if (child.id === 'products') {
                            return canSeeMenuItem(child.role_subject) && productsEnabled;
                        }

                        if (child.id === 'site-builder' || child.id === 'business-profile-card') {
                            return canSeeMenuItem(child.role_subject) && siteBuilderEnabled;
                        }

                        if (child.id === 'review-management') {
                            // if legacy rep management enabled, new one is disabled
                            return !legacyReviewManagement && canSeeMenuItem(child.role_subject, 'update');
                        }

                        if (child.id === 'loyalty_program') {
                            return canSeeMenuItem(child.role_subject) && loyaltyProgramEnabled;
                        }

                        return canSeeMenuItem(child.role_subject);
                    });
                    return filteredChildren.length ? [...prev, { ...current, children: filteredChildren }] : prev;
                }

                if (current.type === 'item' && !current.children) {
                    return canSeeMenuItem(current.role_subject) || !current.role_subject ? [...prev, current] : prev;
                }

                return prev;
            }, []),
        [canSeeMenuItem, isProviderOrFrontDesk, legacyReviewManagement, productsEnabled, siteBuilderEnabled, loyaltyProgramEnabled]
    );

    const availableSettings = useMemo(() => filteredItems.find((item) => item.id === 'settings-index')?.children ?? [], [filteredItems]);

    return { filteredItems, availableSettings };
};

export default useAvailableMenuItems;
